// Session-management API layer for the Devices & sessions screen.
//
// These endpoints are served by vfco-auth-package (RegisterRoutes), not the
// Portal's own OpenAPI surface, so they aren't in the generated api/schema.ts.
// We mirror the package's JSON shapes here as local interfaces — the same
// pattern AuthVerifyPage uses for /api/auth/verify. (Follow-up: fold these
// into the Portal OpenAPI so the binding is generated, not hand-kept.)

import { api } from './api';

// Timestamps are RFC3339 strings (Go time.Time over JSON).
export interface SessionView {
  id: string;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string;
  ipAddress: string;
  expiresAt: string;
  // Server-computed: true for the session whose refresh-token cookie made
  // this request. The current session cannot be revoked via DELETE (403);
  // it must go through POST /api/auth/logout instead.
  current: boolean;
}

interface SessionsListResp {
  sessions: SessionView[];
}

export interface RevokeResp {
  message: string;
  sessionsRemaining: number;
}

export interface SignOutOthersResult {
  revoked: number;
  failed: number;
}

// GET /api/users/me/sessions — the user's active sessions. The safe GET also
// seeds the CSRF token (captured by the api wrapper) for the revoke DELETEs
// that follow.
export async function listSessions(signal?: AbortSignal): Promise<SessionView[]> {
  const resp = await api<SessionsListResp>('/api/users/me/sessions', { signal });
  return resp.sessions ?? [];
}

// DELETE /api/users/me/sessions/{id} — revoke one non-current session,
// freeing a device-cap slot. 403 CANNOT_REVOKE_CURRENT_SESSION if id is the
// current session; callers must route the current device through logout.
export function revokeSession(id: string): Promise<RevokeResp> {
  return api<RevokeResp>(`/api/users/me/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// POST /api/auth/device-compromise — report the device behind a session as
// compromised ("this wasn't me", ). The server resolves the session to a
// device fingerprint, flags it, and tombstones its live sessions: the device's
// next API call is met with 401 + X-Compromise-Signal (wiping its offline
// store) and its next sign-in must re-enroll a passkey. Unlike revoke, this does
// not drop the session immediately — the kill happens when the device next
// connects, which is also what triggers the wipe.
export function reportDeviceCompromised(sessionId: string): Promise<{ status: string }> {
  return api<{ status: string }>('/api/auth/device-compromise', {
    method: 'POST',
    body: { sessionId },
  });
}

// Sign out every device except the current one. The backend has no single
// "logout others" endpoint, so we revoke each non-current session in parallel
// and tally outcomes; the caller re-fetches the list to show ground truth.
export async function signOutOtherDevices(sessions: SessionView[]): Promise<SignOutOthersResult> {
  const others = sessions.filter((s) => !s.current);
  const results = await Promise.allSettled(others.map((s) => revokeSession(s.id)));
  let revoked = 0;
  let failed = 0;
  for (const r of results) {
    if (r.status === 'fulfilled') revoked += 1;
    else failed += 1;
  }
  return { revoked, failed };
}
